import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Loader from '../../../Loader';
import Pagination from '../../Pagination';
import SecondaryButton from '../../../Buttons/SecondaryButton/SecondaryButton';
import SamplesList from '../SamplesList';
import './style.scss';

export default function SamplesListContainer() {
  const { t } = useTranslation();
  const dataUser = useSelector((state) => state?.auth?.userDataDetail);
  const [isLoading, setIsLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [error, setError] = useState(null);

  // Filtri
  const [filters, setFilters] = useState({
    number: '',
    status: '',
    startDate: '',
    endDate: '',
  });

  // Paginazione
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Reset filtri
  const resetFilter = () => {
    setFilters({
      number: '',
      status: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  useEffect(() => {
    const fetchSamples = async () => {
      if (!dataUser?.ACCOUNTNUM) return;
      setIsLoading(true);
      try {
        // Chiamata API esterna (webapp)
        const respExternal = await axios.get(`https://digitex.filmar.it/admin/api/v1/get-orders-sample-list/${dataUser.ACCOUNTNUM}`, {
          headers: {
            Authorization: `Bearer ${dataUser?.token}`,
          },
        });
        const externalPayload = respExternal?.data?.data ?? respExternal?.data ?? [];

        // Chiamata API interna (database del sito)
        const respInternal = await axios.get(`/v1/get-order-sample-db-site/${dataUser.ACCOUNTNUM}`, {
          headers: {
            Authorization: `Bearer ${dataUser?.token}`,
          },
        });
        const internalPayload = respInternal?.data?.data ?? respInternal?.data ?? [];

        // Combina i risultati delle due API
        const combinedSamples = [
          ...(Array.isArray(externalPayload) ? externalPayload : []),
          ...(Array.isArray(internalPayload) ? internalPayload : [])
        ];

        // Ordina per data decrescente (più recenti prima)
        const sortedSamples = combinedSamples.sort((a, b) => {
          const dateA = new Date(a?.DataOrdine ?? a?.order_date ?? a?.created_at ?? 0);
          const dateB = new Date(b?.DataOrdine ?? b?.order_date ?? b?.created_at ?? 0);
          return dateB - dateA; // Ordine decrescente (più recenti prima)
        });

        setSamples(sortedSamples);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSamples();
  }, [dataUser?.ACCOUNTNUM]);

  // Applica i filtri usando useMemo per efficienza
  const filteredSamples = useMemo(() => {
    return samples.filter(s => {
      const sampleDate = s?.DataOrdine ?? s?.created_at ?? '';
      const sampleNumber = s?.OrderIdAX ?? s?.id ?? '';
      const sampleStatus = s?.StatoOrdine ?? '';

      return (
        (filters.number === '' || sampleNumber.toString().toLowerCase().includes(filters.number.toLowerCase())) &&
        (filters.status === '' || sampleStatus.toLowerCase() === filters.status.toLowerCase()) &&
        (filters.startDate === '' || sampleDate >= filters.startDate) &&
        (filters.endDate === '' || sampleDate <= filters.endDate)
      );
    });
  }, [samples, filters]);

  // Calcola gli elementi da mostrare per la pagina corrente
  const startIndex = (page - 1) * perPage;
  const currentPageSamples = filteredSamples.slice(startIndex, startIndex + perPage);
  const totalPages = Math.ceil(filteredSamples.length / perPage);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div className="error">{t('Errore nel recupero delle campionature')}</div>;
  }

  return (
    <div className="samples-list-container">
      <div className="orders-filter">
        <div className="left">
          <div className="search_field">
            <input
              type="text"
              name="number"
              placeholder={t('search_order') }
              value={filters.number}
              onChange={handleFilterChange}
            />
          </div>
          <div className="search_field date">
            <span>{t('common.date_from')}:</span>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="search_field date">
            <span>{t('common.date_to')}:</span>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="search_field">
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">{t('Stato') || 'Stato'}</option>
              <option value="sent">{t('Inviato')}</option>
              <option value="draft">{t('In Bozza')}</option>
            </select>
          </div>
          <div className="search_field date">
            <SecondaryButton onClick={resetFilter}>{t('common.filter_reset')}</SecondaryButton>
          </div>
        </div>
        <div className="right">
          <div className="per-page">
            <span>{t('order_number')}</span>
            <select name="per_page" id="per_page" value={perPage} onChange={(event) => { setPerPage(parseInt(event.target.value)); setPage(1); }}>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
            </select>
          </div>
        </div>
      </div>

      {/* RISULTATI */}
      <SamplesList samples={currentPageSamples} />

      {/* PAGINAZIONE */}
      {totalPages > 1 ? (
        <Pagination
          className="pagination-bar"
          currentPage={page - 1}
          totalCount={filteredSamples.length}
          pageSize={perPage}
          onPageChange={(page) => setPage(page + 1)}
          onPageClickScrollHref="samples-table"
        />
      ) : null}
    </div>
  );
}